import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const NATARAJA_IMAGE = "https://customer-assets.emergentagent.com/job_nataraja-dance/artifacts/deq4eamm_IMG_2545.jpeg";

export const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      className="hero-section relative flex items-center"
      data-testid="hero-section"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 hero-gradient" />
      
      {/* Content Container */}
      <div className="container-custom relative z-10 py-32 md:py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center min-h-screen">
          {/* Left - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-left space-y-8 pt-20 lg:pt-0"
          >
            <div className="space-y-4">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-gold/80 font-manrope text-sm tracking-[0.3em] uppercase"
              >
                Where Devotion Meets Art
              </motion.p>
              
              <h1 className="font-cinzel text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight">
                <span className="text-gold-gradient">Shri Kala</span>{" "}
                <span className="text-white">Vrinda</span>
              </h1>
              
              <p className="font-playfair text-xl md:text-2xl text-white/70 italic">
                A Sacred Journey into Bharatanatyam
              </p>
            </div>

            <p className="font-manrope text-white/60 text-base md:text-lg leading-relaxed max-w-xl">
              Discover the divine art of Bharatanatyam under the guidance of Vidhushi Varsha R. 
              Begin your spiritual and artistic journey with classical Indian dance rooted in 
              centuries of tradition.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection("#courses")}
                className="btn-gold font-manrope text-sm tracking-wider"
                data-testid="explore-courses-btn"
              >
                Explore Courses
              </Button>
              <Button
                onClick={() => scrollToSection("#enquiry")}
                variant="outline"
                className="btn-gold-outline font-manrope text-sm tracking-wider"
                data-testid="start-journey-btn"
              >
                Start Your Journey
              </Button>
            </div>
          </motion.div>

          {/* Right - Nataraja Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="relative flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Glow effect behind image */}
              <div className="absolute inset-0 bg-gradient-radial from-gold/20 via-gold/5 to-transparent rounded-full blur-3xl scale-110" />
              
              <img
                src={NATARAJA_IMAGE}
                alt="Lord Nataraja - The Cosmic Dancer"
                className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl nataraja-glow"
                data-testid="nataraja-image"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer"
        onClick={() => scrollToSection("#about")}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="text-gold/60 hover:text-gold transition-colors"
        >
          <ChevronDown size={32} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
